import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO, useTicker } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, WeekdayMilestoneGrid, buildAppFaqLd, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "Retirement Countdown: Live Working Days & Seconds Until You Retire";
const DESC = "See a real-time ticking countdown of working days, hours, minutes and seconds remaining until your chosen retirement age, plus retirement year and pension milestones.";

const FAQS = [
  { q: "How is the retirement countdown calculated?", a: "We add your chosen retirement age to your DOB, get the exact retirement date, then subtract the current moment second-by-second — no simplifying assumptions." },
  { q: "Why does 'working days' differ from calendar days?", a: "Working days exclude weekends (Sat/Sun). It's a closer estimate of the remaining payslips you'll actually receive." },
  { q: "What is the default retirement age?", a: "60 years is the global median. You can adjust — India Central Govt uses 60, US Social Security full age is 67, UK State Pension is 66 rising to 67." },
  { q: "Does the ticker keep counting when I switch tabs?", a: "Yes. The countdown recomputes from absolute timestamps every second, so it stays accurate even after you return to the tab." },
];

const TOC = [
  { id: "calculator", label: "Live Countdown" },
  { id: "ages", label: "Global Retirement Ages" },
  { id: "planning", label: "Financial Planning" },
  { id: "faqs", label: "FAQs" },
];

export const Route = createFileRoute("/age-calculator/retirement-countdown")({
  head: () => leafHead({
    path: "/age-calculator/retirement-countdown", title: TITLE, description: DESC,
    scripts: [
      buildAppFaqLd({ name: "Retirement Countdown Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "Retirement Countdown", url: "https://drhint.com/age-calculator/retirement-countdown" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [retAge, setRetAge] = useState(60);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();
  useTicker(revealed);

  const retDate = useMemo(() => {
    if (!dob) return null;
    const d = new Date(dob);
    if (isNaN(d.getTime())) return null;
    const r = new Date(d);
    r.setFullYear(d.getFullYear() + retAge);
    return r;
  }, [dob, retAge]);

  const live = useMemo(() => {
    if (!retDate) return null;
    const now = new Date();
    const ms = retDate.getTime() - now.getTime();
    const past = ms <= 0;
    const abs = Math.abs(ms);
    const days = Math.floor(abs / 86400000);
    const hours = Math.floor((abs % 86400000) / 3600000);
    const minutes = Math.floor((abs % 3600000) / 60000);
    const seconds = Math.floor((abs % 60000) / 1000);
    // Working days: weekdays only between now and retDate
    let workDays = 0;
    if (!past) {
      const cur = new Date(now);
      for (let i = 0; i < days; i++) {
        const wd = cur.getDay();
        if (wd !== 0 && wd !== 6) workDays += 1;
        cur.setDate(cur.getDate() + 1);
      }
    }
    return { past, days, hours, minutes, seconds, workDays };
  }, [retDate, revealed]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyText = live && retDate ? `Retirement on ${retDate.toDateString()} — ${live.days.toLocaleString()} calendar days / ${live.workDays.toLocaleString()} working days left.` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / Retirement Countdown
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Live-ticking retirement countdown — kitne <strong>working days aur seconds</strong> baaki hain aap ki chuni hui retirement age tak. Pension planning ke liye deep dive <Link to="/age-calculator/retirement-pension" className="text-primary underline">here</Link> ya <Link to="/age-calculator" className="text-primary underline">age hub</Link>.
        </p>
      </section>
      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
            <div>
              <div className="text-sm font-semibold mb-2">Retirement Age</div>
              <div className="flex flex-wrap gap-1.5">
                {[55, 58, 60, 62, 65, 67, 70].map((a) => (
                  <button key={a} type="button" onClick={() => { setRetAge(a); reset(); }} className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${retAge === a ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"}`}>{a}</button>
                ))}
              </div>
              <input type="range" min={45} max={75} value={retAge} onChange={(e) => { setRetAge(+e.target.value); reset(); }} className="w-full mt-4 accent-primary" />
              <div className="text-center text-xs text-muted-foreground">Chosen: <strong>{retAge}</strong> years</div>
            </div>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Start Live Countdown" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={300}>
            {revealed && live && retDate ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Days" value={live.past ? `${live.days.toLocaleString()} ago` : live.days.toLocaleString()} />
                  <ResultCard label="Hours" value={live.hours} />
                  <ResultCard label="Minutes" value={live.minutes} />
                  <ResultCard label="Seconds" value={String(live.seconds).padStart(2, "0")} sub="ticking live" />
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <ResultCard label="Working Days Left" value={live.workDays.toLocaleString()} sub="Weekdays only" />
                  <ResultCard label="Retirement Date" value={retDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })} sub={retDate.toLocaleDateString("en-US", { weekday: "long" })} />
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy Countdown" /></div>
                <LifeProgressBar dob={dob} expectancy={retAge + 15} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter your DOB and start the live countdown.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="ages" title="Statutory Retirement Ages Around the World">
        <table>
          <thead><tr><th>Country</th><th>Standard</th><th>Early / Special</th></tr></thead>
          <tbody>
            <tr><td>India (Central Govt)</td><td>60</td><td>50 with VRS</td></tr>
            <tr><td>Pakistan (Federal)</td><td>60</td><td>55 with Golden Handshake</td></tr>
            <tr><td>United States (Social Security)</td><td>67</td><td>62 with reduced benefit</td></tr>
            <tr><td>United Kingdom</td><td>66 → 67</td><td>55 for private pensions</td></tr>
            <tr><td>Germany</td><td>67</td><td>63 after 45 contribution years</td></tr>
          </tbody>
        </table>
      </AnchorSection>

      <AnchorSection id="planning" title="Turning the Countdown Into a Financial Plan">
        <p>Every remaining working day is a <strong>compounding opportunity</strong>. A 30-year-old with 30 years to retirement who saves ₹25,000 / month at 12% CAGR ends up with over ₹8 crore — half of which comes from just the last 8 years. Retirement countdowns work best when paired with a matching SIP tracker so you can watch both the clock and the corpus tick.</p>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}